import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import CustomTable, { CustomColumnDef } from '../../../../components/customTable/CustomTable'
import { OptionExpiryType } from '../../../../reducer/options.reducer'
import { useDispatch, useSelector } from 'react-redux'
import { selectOptionChainTableView, selectOptionChainVisibleStrikes, selectShowOptionChainQuantityInLots } from '../../../../selectors/common'
import { selectUnderlyingOptionsTradeData, selectUnderlyingOptionsTradeExpiryData } from '../../../../selectors/options'
import {
    selectOptionChainColumns,
    selectOptionChainTimeInterval,
    selectPrimarySecurity
} from '../../../../selectors/home'
import { getSecurityAsync, getSecurityFromCache, getSecurityKey } from '../../../../util/security'
import { findClosestSubarray, isStrikeRoundingNeeded } from '../../../../util/options'
import { CustomOptionSecurityType, OIChange, optionsTradesDataType, optionTradeDataType } from '../../../typings/options.typings'
import { colMinWidth, colsWidth, getOptionChainCellIdentityKey, optionColumnOptions, spanCols } from '../optionChainTable'
import { Typography } from '@mui/material'
import { v4 as uuid } from 'uuid'
import { tradeOrderReferencePriceFromOption } from '../../../../util/orderBookPrice'
import Loading from '../../../../components/common/Loading'
import { selectBasketAnalyzeDetails, selectOrdersData, selectOrdersHistory } from '../../../../selectors/orders'
import CustomRenderStrike from './CustomStrikeRender'
import { OptionChainPositionContext } from './OptionChainPositionContext'
import ColumnElementRender from './ColumnElementRender'
import { Candle, Security } from '../../../../proto/generated/candle-data-depot-common'
import { useCandleChangeManager } from '../../../../components/candleChangeManager/candleChangeManagerContext'
import { useCombineBasketAnalyzeAndLegOrders } from '../../../../hooks/useCombineBasketAnalyzeAndLegOrders'
import { getNearestTimeBucketStartTimeStampToGivenTime } from '../../../../util/chart'
import { selectExchangeTradingSessions } from '../../../../selectors/chart'
import { SuperChartConfiguration_TimeBucket } from '../../../../proto/generated/superchart-page'
import { isOptionChainLiveView, OptionChainViewKey } from '../../../../assets/constants'
import { selectSimulatorDate } from '../../../../selectors/simulator'
import { setSimulatorOrderDataFunction } from '../../../../reducer/simulator.reducer'
import { ActionType, useUndoRedoContext } from '../../../../components/UndoRedoFunctonalities/UndoRedo.context'
import { OptionChainTableView } from '../../fnoHeader/optionsChainSettingsDialog'
import { OptionType } from '../../../../proto/generated/security'
import { Broker_OrderType, Order, TransactionType } from '../../../../proto/generated/order'
import {
    selectAddOrderEnabled,
    selectBasketId,
    selectBatchId,
    selectOptionChainRowOrderSide,
    selectOrderType,
    selectStrategyBuilderMode,
    selectTradeOrders
} from '../../../../selectors/strategyBuilder'
import { selectRightSidebarWidget } from '../../../../selectors/common'
import store from '../../../../store'
import { OptionChainOrderHighlightContext } from './OptionChainOrderHighlightContext'
import { OptionChainOrderClickContext } from './OptionChainOrderClickContext'
import OptionChainTableRow from './OptionChainTableRow'
import { TableRowComponentProps } from '../../../../components/customTable/CustomTable'
import { selectPositions } from '../../../../selectors/positions'
import { setBatchId, updateTradeOrders } from '../../../../reducer/strategyBuilder.reducer'
import commonActions from '../../../../actions/common.actions'
import { WidgetListItems } from '../../../../util/sidebar'
import { getCurrentOrderLtpPriceFromData, isOrderBatchComplete } from '../../../../util/tradepanel'

export type OptionChainExpandedTableProps = {
    row: OptionExpiryType,
    index: number,
    noOfColumnsVisible: number
    bidAskColumnWidth: number
    strikeColWidth: number
    optionChainViewKey: OptionChainViewKey
    showSingleExpiry: boolean
    isMirrorScroll?: boolean
    type: 'strikeCol' | 'putOption' | 'callOption' | 'itmOption' | 'otmOption'
    /** Widget dashboard: read trade data for this underlying key instead of global primary. */
    underlyingToken?: string
}

const OptionChainExpandedTable = ({
    row,
    index,
    noOfColumnsVisible,
    bidAskColumnWidth,
    strikeColWidth,
    optionChainViewKey,
    showSingleExpiry = false,
    isMirrorScroll = false,
    type,
    underlyingToken: underlyingTokenOverride,
}: OptionChainExpandedTableProps) => {
    const dispatch = useDispatch()
    const { push } = useUndoRedoContext()
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const security = useMemo(() => expressionMode ? null : (underlying || primary), [underlying, primary, expressionMode])
    const token = useMemo(
        () => underlyingTokenOverride || getSecurityKey(security?.exchange ?? 0, security?.code ?? ''),
        [underlyingTokenOverride, security?.exchange, security?.code]
    )
    const resolvedUnderlying = useMemo(() => {
        if (underlyingTokenOverride) {
            return getSecurityFromCache(underlyingTokenOverride) ?? security
        }
        return security
    }, [underlyingTokenOverride, security])
    const selectedExpiry = row?.expiry ?? -1
    const optionExpiries = useSelector(selectUnderlyingOptionsTradeData(token))
    const visibleStrikes = useSelector(selectOptionChainVisibleStrikes)
    const optionsDataForExpiry = useSelector(selectUnderlyingOptionsTradeExpiryData(token, selectedExpiry))
    const optionsDataForExpiryRef = useRef(optionsDataForExpiry)
    const orderHistory = useSelector(selectOrdersHistory);
    const orderData = useSelector(selectOrdersData)
    const { orderState = 'order', basketId: oldBasketId } = orderData
    const { allOrders, allOrdersPrices, orders, analyzeOrders = [] } = useCombineBasketAnalyzeAndLegOrders()
    const basketId = useSelector(selectBasketId);
    const positions = useSelector(selectPositions);
    const builderMode = useSelector(selectStrategyBuilderMode);
    const builderOrderType = useSelector(selectOrderType);
    const simulatorTimestamp = useSelector(selectSimulatorDate)
    const selectedColumns = useSelector(selectOptionChainColumns(optionChainViewKey, showSingleExpiry ? 'extended' : 'normal'))
    const basketAnalyzeDetails = useSelector(selectBasketAnalyzeDetails)
    const quantityInLots: boolean = useSelector(selectShowOptionChainQuantityInLots)
    const timebucket = useSelector(selectOptionChainTimeInterval(optionChainViewKey, showSingleExpiry ? 'extended' : 'normal'))
    const optionSecurities = useRef<Security[]>([])
    const lastSubSecurities = useRef<Security[] | undefined>(undefined)
    const optionSecuritiesCandles = useRef<Record<string, Candle[]>>({})
    const prevOptionSecuritiesCandles = useRef<Record<string, Candle[]>>({})
    const { manager: candleChangeManager } = useCandleChangeManager()
    const exchangeTradingSessions = useSelector(selectExchangeTradingSessions)
    const selectedOptionChainTableView = useSelector(selectOptionChainTableView)
    const addOrderEnabled = useSelector(selectAddOrderEnabled);
    const [skeletalLoading, setSkeletalLoading] = useState(false)
    const [strikeRoundingRequired, setStrikeRoundingRequired] = useState(true)

    const historicalOrderMap = useMemo(() => {
        if (optionChainViewKey !== OptionChainViewKey.HISTORICAL) return null
        const result: Record<string, Record<'call' | 'put', 'buy' | 'sell'>> = {}
        orders.filter(o => o.timeStamp === simulatorTimestamp && !o.traded).forEach(order => {
            if (order.contractType === 'OPTION') {
                if (order.callput === 'call') {
                    result[order.data.key] = {
                        ...result[order.data.key],
                        call: order.type
                    }
                } else {
                    result[order.data.key] = {
                        ...result[order.data.key],
                        put: order.type
                    }
                }
            }
        })
        return result
    }, [orders, simulatorTimestamp, optionChainViewKey])

    // const analyzeOrderKeytoOrderTypeMap = useMemo(() => {
    //     const result: Record<string, Record<'call' | 'put', 'buy' | 'sell'>> = {}
    //     analyzeOrders.forEach(order => {
    //         if (order.contractType === 'OPTION') {
    //             if (order.callput === 'call') {
    //                 result[order.data.key] = {
    //                     ...result[order.data.key],
    //                     call: order.type
    //                 }
    //             } else {
    //                 result[order.data.key] = {
    //                     ...result[order.data.key],
    //                     put: order.type
    //                 }
    //             }
    //         }
    //     })
    //     return result
    // }, [analyzeOrders])

    useEffect(() => {
        if (candleChangeManager && ((JSON.stringify(optionSecurities.current) !== JSON.stringify(lastSubSecurities.current)) || (JSON.stringify(optionSecuritiesCandles.current) !== JSON.stringify(prevOptionSecuritiesCandles.current)))) {
            if (optionSecurities.current.length) {
                candleChangeManager.addSecurities(optionSecurities.current, optionSecuritiesCandles.current)
                candleChangeManager.timebucket = timebucket
                lastSubSecurities.current = optionSecurities.current
                prevOptionSecuritiesCandles.current = optionSecuritiesCandles.current
            }
        }
    }, [visibleStrikes, optionsDataForExpiry, timebucket, exchangeTradingSessions, candleChangeManager])

    useEffect(() => {
        return () => {
            if (candleChangeManager && optionSecurities.current) {
                const secKeys = optionSecurities.current.map(security => getSecurityKey(security.exchange, security.code))
                candleChangeManager.removeSecurities(secKeys)
            }
        }
    }, [])

    useEffect(() => {
        optionsDataForExpiryRef.current = optionsDataForExpiry
    }, [optionsDataForExpiry])

    // const visibleColumnsCountWithSpanCols = useMemo(() => {
    //     let sudoColsCount = 0;
    //     ([...selectedColumns].reverse()).slice(-1 * noOfColumnsVisible).forEach((column: string) => {
    //         column.split('|').forEach((subColumn) => {
    //             sudoColsCount += colsWidth(showSingleExpiry)[subColumn] ?? 1
    //         })
    //     })
    //     return sudoColsCount
    // }, [selectedColumns, noOfColumnsVisible, showSingleExpiry])


    const record = useMemo(() => {
        const newData: optionsTradesDataType = JSON.parse(JSON.stringify(optionsDataForExpiry))

        if (visibleStrikes !== 'all') {
            if (Number(visibleStrikes) < newData.trades.length)
                newData.trades = findClosestSubarray(newData.trades, newData.atmStrike, Number(visibleStrikes))
        } else {
            newData.trades = newData.trades.filter((trade) => ((trade.callOption?.openInterest ?? 0) > 0 || (trade.putOption?.openInterest ?? 0) > 0))
        }
        const securities: Security[] = []
        const candleRecord: Record<string, Candle[]> = {}
        const exchangeForSessions = resolvedUnderlying?.exchange
            ?? newData.trades[0]?.callOption?.security?.exchange
            ?? newData.trades[0]?.putOption?.security?.exchange
        const tradingSessions = exchangeTradingSessions?.find(v => v?.exchange === exchangeForSessions)?.tradingSessions
        if (!tradingSessions) return newData
        newData.trades.forEach((trade) => {
            const startTimestamp = getNearestTimeBucketStartTimeStampToGivenTime(
                tradingSessions,
                trade.timeStamp,
                {
                    timeSeries: timebucket.timeSeries,
                    timeValue: timebucket.value
                } as SuperChartConfiguration_TimeBucket
            )
            if (trade.callOption?.security) {
                const security = trade.callOption.security
                const secKey = getSecurityKey(security.exchange, security.code)
                securities.push(security)
                candleRecord[secKey] = [{
                    startTimestamp: 0,
                    close: trade.callOption.candleChange?.previousLtp ?? 0,
                    openInterest: trade.callOption.candleChange?.previousOi ?? 0
                } as Candle, {
                    startTimestamp: startTimestamp ?? 0,
                    close: trade.callOption.candleChange?.currentLtp ?? 0,
                    impliedVolatility: trade.callOption.impliedVolatility,
                    optionGreeks: trade.callOption.optionGreeks,
                    topBid: trade.callOption.topBid,
                    topAsk: trade.callOption.topAsk,
                    openInterest: trade.callOption.openInterest,
                    volume: trade.callOption.volume
                } as Candle]
            }
            if (trade.putOption?.security) {
                const security = trade.putOption.security
                const secKey = getSecurityKey(security.exchange, security.code)
                securities.push(security)
                candleRecord[secKey] = [{
                    startTimestamp: 0,
                    close: trade.putOption.candleChange?.previousLtp ?? 0,
                    openInterest: trade.putOption.candleChange?.previousOi ?? 0
                } as Candle, {
                    startTimestamp: startTimestamp ?? 0,
                    close: trade.putOption.candleChange?.currentLtp ?? 0,
                    impliedVolatility: trade.putOption.impliedVolatility,
                    optionGreeks: trade.putOption.optionGreeks,
                    topBid: trade.putOption.topBid,
                    topAsk: trade.putOption.topAsk,
                    openInterest: trade.putOption.openInterest,
                    volume: trade.putOption.volume
                } as Candle]
            }
        })
        if (securities.length) {
            optionSecurities.current = securities
            optionSecuritiesCandles.current = candleRecord
        }
        return newData
    }, [exchangeTradingSessions, optionsDataForExpiry, resolvedUnderlying, timebucket, visibleStrikes])

    useEffect(()=>{
        if(record.loading) {
            setSkeletalLoading(true)
        } else {
            setTimeout(()=>{
                setSkeletalLoading(false)
            }, 200)
        }
    }, [record.loading])

    useEffect(() => {
        if (isStrikeRoundingNeeded(record.trades.map((trade) => trade.strikeCol.strike), security?.priceMultiplier)) {
            setStrikeRoundingRequired(true)
        } else {
            setStrikeRoundingRequired(false)
        }
    }, [record])

    type StrikePositionMapType = Record<number, {
        call: { type: 'buy' | 'sell'; quantity: number }
        put: { type: 'buy' | 'sell'; quantity: number }
    }>
    const [strikeToPositionMap, setStrikeToPositionMap] = useState<StrikePositionMapType>({})
    useEffect(() => {
        const expiry = record.expiration?.expiry?.expiry
        if (!expiry) return
        const positionsToProcess = positions.filter(p => p.closePosition !== 0)
        if (!positionsToProcess.length) {
            setStrikeToPositionMap({})
            return
        }
        Promise.all(
            positionsToProcess.map((position) =>
                getSecurityAsync(getSecurityKey(position.security.exchange, position.security.code))
            )
        ).then((securities) => {
            const map: StrikePositionMapType = {}
            positionsToProcess.forEach((position, i) => {
                const security = securities[i]
                if (!security || security?.expiry?.expiry !== expiry) return
                const existingData = map?.[security?.strikePrice]
                let callData = existingData?.call
                let putData = existingData?.put
                const positionType = position.closePosition > 0 ? 'buy' : 'sell'
                const positionQuantity = position.closePosition
                if (security.optionType === OptionType.CE) {
                    if (!callData) {
                        callData = { type: positionType, quantity: positionQuantity }
                    } else {
                        if (callData.type === positionType) {
                            callData.quantity += positionQuantity
                        } else {
                            if (callData.quantity > positionQuantity) {
                                callData.quantity -= positionQuantity
                            } else if (callData.quantity < positionQuantity) {
                                callData.type = positionType
                                callData.quantity = positionQuantity - callData.quantity
                            } else {
                                callData = undefined
                            }
                        }
                    }
                } else if (security.optionType === OptionType.PE) {
                    if (!putData) {
                        putData = { type: positionQuantity > 0 ? 'buy' : 'sell', quantity: positionQuantity }
                    } else {
                        if (putData.type === positionType) {
                            putData.quantity += positionQuantity
                        } else {
                            if (putData.quantity > positionQuantity) {
                                putData.quantity -= positionQuantity
                            } else if (putData.quantity < positionQuantity) {
                                putData.type = positionType
                                putData.quantity = positionQuantity - putData.quantity
                            } else {
                                putData = undefined
                            }
                        }
                    }
                }
                map[security?.strikePrice] = { call: callData, put: putData }
            })
            setStrikeToPositionMap(map)
        })
    }, [positions, record])

    const expiryToMaxPricePercentChange: [number, number] = useMemo(() => {
        const maxPricePercentChanges: [number, number] = record.trades.map((tr) => [tr.callOption?.ltpChangePercent ?? 0, tr.putOption?.ltpChangePercent ?? 0] as [number, number]).reduce((prev: [number, number], curr: [number, number]) => {
            return [Math.max(prev[0], Math.abs(curr[0] ?? 0)), Math.max(prev[1], Math.abs(curr[1] ?? 0))]
        }, [0, 0])
        return maxPricePercentChanges
    }, [record])

    const expiryToMaxOiChange: [number, number] = useMemo(() => {
        const maxPricePercentChanges: [number, number] = record.trades.map((tr) => {
            const callOiChangeData: OIChange | undefined = tr.callOption?.oiChanges
            const callOiChangeValue = callOiChangeData?.oiChange?.change ?? 0
            const putOiChangeData: OIChange | undefined = tr.putOption?.oiChanges
            const putOiChangeValue = putOiChangeData?.oiChange?.change ?? 0
            return [callOiChangeValue, putOiChangeValue] as [number, number]
        }).reduce((prev: [number, number], curr: [number, number]) => {
            return [Math.max(prev[0], Math.abs(curr[0] ?? 0)), Math.max(prev[1], Math.abs(curr[1] ?? 0))]
        }, [0, 0])

        return maxPricePercentChanges
    }, [record])

    const handleOptionOrderClick = useCallback(async (passedType: 'buy' | 'sell' | undefined, strike: number, optionType: 'call' | 'put' | 'otm' | 'itm') => {
        let type = passedType
        if (isOptionChainLiveView(optionChainViewKey)) {
            const state = store.getState()
            const tradeOrders = selectTradeOrders(state)
            const batchOrderHistoryMap = orderHistory.reduce((map, order) => {
                map[order.orderId] = order
                return map
            }, {} as Record<string, Order>)
            const currentBatchOrders = tradeOrders.filter(
                order =>
                    !batchOrderHistoryMap[order.clientOrderId] ||
                    batchOrderHistoryMap[order.clientOrderId].batchId === selectBatchId(state)
            )
            const isBatchComplete = isOrderBatchComplete(currentBatchOrders, batchOrderHistoryMap)
            const record = optionsDataForExpiryRef.current.trades.find(t => t.strikeCol.strike === strike)
            let desiredOptionData: CustomOptionSecurityType = null;
            if (optionType === 'itm') {
                desiredOptionData = record.itmOption;
            } else if (optionType === 'otm') {
                desiredOptionData = record.otmOption;
            } else if (optionType === 'call') {
                desiredOptionData = record.callOption;
            } else if (optionType === 'put') {
                desiredOptionData = record.putOption;
            }
            if (!desiredOptionData?.security) return
            const securityKey = getSecurityKey(desiredOptionData.security.exchange, desiredOptionData.security.code)

            if (isBatchComplete) {
                dispatch(setBatchId(uuid()));
            }

            const existingOrder = tradeOrders.find(o => o.security.code === desiredOptionData.security.code);
            if (existingOrder && existingOrder.referenceOrderId) return;
            if (type === undefined) {
                type = !existingOrder ? 'buy' : (existingOrder.transactionType === TransactionType.BUY ? "sell" : undefined)
            }

            const existingOrderType = existingOrder ? (existingOrder.transactionType === TransactionType.BUY ? 'buy' : 'sell'): undefined;
            if (!isBatchComplete && (!type || existingOrderType === type)) {
                dispatch(updateTradeOrders(tradeOrders.filter(tO => tO.clientOrderId !== existingOrder?.clientOrderId)))
            } else if (!isBatchComplete && existingOrder) {
                dispatch(updateTradeOrders(tradeOrders.map(tO => {
                    if (tO.clientOrderId === existingOrder?.clientOrderId) {
                        return {
                            ...tO,
                            transactionType: type === "buy" ? TransactionType.BUY : TransactionType.SELL
                        }
                    }
                    return tO;
                })))
            } else {
                let security = existingOrder?.security ?? getSecurityFromCache(securityKey) ?? desiredOptionData.security
                if (!security) {
                    security = await getSecurityAsync(securityKey)
                }
                if (!security) return;
                dispatch(updateTradeOrders([
                    ...tradeOrders,
                    {
                        security,
                        clientOrderId: uuid(),
                        quantity: security.lotSize??1,
                        selected: true,
                        transactionType: type === "buy" ? TransactionType.BUY : TransactionType.SELL,
                        price: tradeOrderReferencePriceFromOption(
                            type === "buy" ? TransactionType.BUY : TransactionType.SELL,
                            desiredOptionData
                        )
                    }
                ]))
            }
            if (selectRightSidebarWidget(state) !== WidgetListItems.TRADE) {
                commonActions.dispatchRightSideWidget(dispatch, WidgetListItems.TRADE)
            }
        } else if (optionChainViewKey === OptionChainViewKey.HISTORICAL) {
            const record = optionsDataForExpiryRef.current.trades.find(t => t.strikeCol.strike === strike)
            const exist = allOrders.find(v => v.contractType === 'OPTION' && v.data?.key === record?.key && v.type === type && v.callput === optionType && v.timeStamp === simulatorTimestamp)
            if (!exist) {
                const newOrders = [...allOrders]
                const newOrdersPrices = [...allOrdersPrices]
                const sameSecDifOrder = newOrders.findIndex(v => v.contractType === 'OPTION' && v.data?.key === record?.key && v.callput === optionType && v.timeStamp === simulatorTimestamp)
                if (sameSecDifOrder >= 0) {
                    if (record) {
                        newOrders[sameSecDifOrder] = {
                            ...newOrders[sameSecDifOrder],
                            type,
                            timeStamp: simulatorTimestamp
                        }
                        newOrdersPrices[sameSecDifOrder] = getCurrentOrderLtpPriceFromData(newOrders[sameSecDifOrder], optionExpiries ?? [], [])
                        newOrdersPrices[sameSecDifOrder].timeStamp = simulatorTimestamp
                        push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                        setSimulatorOrderDataFunction({
                            orders: newOrders,
                            orderPrices: newOrdersPrices,
                        })
                    }
                } else {
                    if (record) {
                        const option = optionType === 'call' ? record.callOption : record.putOption
                        let desiredOptionData;
                        if (optionType === 'itm') {
                            desiredOptionData = record.itmOption;
                        } else if (optionType === 'otm') {
                            desiredOptionData = record.otmOption;
                        } else if (optionType === 'call') {
                            desiredOptionData = record.callOption;
                        } else if (optionType === 'put') {
                            desiredOptionData = record.putOption;
                        }
                        const callput = desiredOptionData.security.optionType === OptionType.CE ? 'call' : 'put'
                        newOrders.push({
                            type,
                            security: option?.security,
                            data: record,
                            callput,
                            quantity: 1,
                            expiry: Number(optionsDataForExpiryRef.current.expirySeconds) ?? 0,
                            strike: record.strikeCol.strike,
                            orderDetails: undefined,
                            uuid: uuid(),
                            contractType: 'OPTION',
                            visibility: true,
                            timeStamp: simulatorTimestamp
                        })

                        const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionExpiries ?? [], [])
                        newPrice.timeStamp = simulatorTimestamp

                        newOrdersPrices.push(newPrice)
                        push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                        setSimulatorOrderDataFunction({
                            orders: newOrders,
                            orderPrices: newOrdersPrices,
                        })
                    }
                }
            } else {
                const record = optionsDataForExpiryRef.current.trades.find(t => t.strikeCol.strike === strike)
                if (!record) return
                const newOrders = [...allOrders]
                const newOrdersPrices = [...allOrdersPrices]
                const option = optionType === 'call' ? record.callOption : record.putOption
                let desiredOptionData;
                if (optionType === 'itm') {
                    desiredOptionData = record.itmOption;
                } else if (optionType === 'otm') {
                    desiredOptionData = record.otmOption;
                } else if (optionType === 'call') {
                    desiredOptionData = record.callOption;
                } else if (optionType === 'put') {
                    desiredOptionData = record.putOption;
                }
                const callput = desiredOptionData.security.optionType === OptionType.CE ? 'call' : 'put'
                newOrders.push({
                    type,
                    security: option?.security,
                    data: record,
                    callput,
                    quantity: 1,
                    expiry: Number(optionsDataForExpiryRef.current.expirySeconds) ?? 0,
                    strike: record.strikeCol.strike,
                    orderDetails: undefined,
                    uuid: uuid(),
                    contractType: 'OPTION',
                    visibility: true,
                    timeStamp: simulatorTimestamp
                })

                const newPrice = getCurrentOrderLtpPriceFromData(newOrders[newOrders.length - 1], optionExpiries ?? [], [])
                newPrice.timeStamp = simulatorTimestamp

                newOrdersPrices.push(newPrice)
                push({ prev: { orders: allOrders, ordersPrices: allOrdersPrices }, new: { orders: newOrders, ordersPrices: newOrdersPrices } }, 'simultaor orders changed', ActionType.SIMULATOR_ORDERS_CHANGED)
                setSimulatorOrderDataFunction({
                    orders: newOrders,
                    orderPrices: newOrdersPrices,
                })
            }
        }
    }, [optionChainViewKey, builderMode, basketId, builderOrderType, orderHistory, orderState, allOrders, basketAnalyzeDetails, oldBasketId,
        optionExpiries, token, simulatorTimestamp, allOrdersPrices, push, record.expirationDateKey, dispatch])

    const handleOptionOrderClickRef = useRef(handleOptionOrderClick)
    handleOptionOrderClickRef.current = handleOptionOrderClick
    const stableHandleOptionOrderClick = useCallback(
        (type: 'buy' | 'sell' | undefined, strike: number, optionType: 'call' | 'put' | 'otm' | 'itm') =>
            handleOptionOrderClickRef.current(type, strike, optionType),
        []
    )

    const totalColumnWidthUnits = useMemo(() => {
        let total = 0
        selectedColumns.forEach((column: string) => {
            column.split('|').forEach((subColumn) => {
                total += colsWidth(showSingleExpiry)[subColumn] ?? 1
            })
        })
        return total > 0 ? total : 1
    }, [selectedColumns, showSingleExpiry])

    const getRowClassName = useCallback((record1: optionTradeDataType, index: number) => {
        const strike = record1.strikeCol.strike
        const atmStrike = record.atmStrike
        const sanitizedStrike = String(strike).replace(/\./g, '-')
        const expiryValue = record.expirationDateKey || 'unknown'
        const sanitizedExpiry = String(expiryValue)
            .replace(/\s+/g, '-')
            .replace(/[^a-zA-Z0-9-]/g, '')
        let result: string = `option-chain-row option-chain-row-${sanitizedStrike} expiry-${sanitizedExpiry} `
        // if (analyzeOrderKeytoOrderTypeMap[record1?.key]) {
        //     const data = analyzeOrderKeytoOrderTypeMap[record1?.key]
        //     if (data.call) {
        //         result = result + ` call-analyze-${data.call}-order-row `
        //     }
        //     if (data.put) {
        //         result = result + ` put-analyze-${data.put}-order-row `
        //     }
        // }
        if (atmStrike === strike) {
            result += ' atm-strike upper-active lower-active '
        }
        if (record1.strikeCol.strike >= record.oneSd && index - 1 >= 0 && record.oneSd > record.trades[index - 1].strikeCol.strike) {
            result += ' one-sd-line-row '
        }
        if (record1.strikeCol.strike >= record.minusOneSd && index - 1 >= 0 && record.minusOneSd > record.trades[index - 1].strikeCol.strike) {
            result += ' minus-one-sd-line-row '
        }
        if (record1.strikeCol.strike >= record.twoSd && index - 1 >= 0 && record.twoSd > record.trades[index - 1].strikeCol.strike) {
            result += ' two-sd-line-row '
        }
        if (record1.strikeCol.strike >= record.minusTwoSd && index - 1 >= 0 && record.minusTwoSd > record.trades[index - 1].strikeCol.strike) {
            result += ' minus-two-sd-line-row '
        }
        if (index < record.trades.length - 1) {
            const nextRecord = record.trades[index + 1]
            if (record.trades[index].strikeCol.strike === record.maxPain) result = result + ' max-pain-strike'
            if (atmStrike && nextRecord.strikeCol.strike > atmStrike && strike <= atmStrike) {
                // const table = tableRef.current
                // if (table) {
                //     if (index > 10) {
                // const row = table.querySelector(
                //     `tr[data-row-key="${index - 6}"]`
                // )
                // if (row) {
                //     row.scrollIntoView()
                // }
                //     }
                // }
                return 'underline-row' + result
            } else if (atmStrike && strike > atmStrike) {
                if(type==='otmOption') return result
                return 'lower-active ' + result
            } else if (atmStrike && strike < atmStrike) {
                if(type==='otmOption') return result
                return 'upper-active ' + result
            }
        } else if (index === record.trades.length - 1) {
            if (record.trades[index].strikeCol.strike === record.maxPain) result = result + ' max-pain-strike'
            if (atmStrike && strike > atmStrike) {
                if(type==='otmOption') return result
                return 'lower-active ' + result
            } else if (atmStrike && strike < atmStrike) {
                if(type==='otmOption') return result
                return 'upper-active ' + result
            }
        }
        return result
    }, [type, record.atmStrike, record.maxPain, record.minusOneSd, record.minusTwoSd, record.oneSd, record.trades, record.twoSd, record.expirationDateKey])

    const strikeColumns = useMemo((): CustomColumnDef<optionTradeDataType>[] => 
        (showSingleExpiry || selectedOptionChainTableView === OptionChainTableView.NORMAL) ? [
        ...(type === 'callOption' && noOfColumnsVisible > 0 ? [...([...selectedColumns]).map((val: string, i: number, arr: string[]) => {
            const currentindex = (isMirrorScroll) ? (arr.length - i - 1) : i
            const v = (isMirrorScroll) ? arr[currentindex] : val
            const colSpan = spanCols.includes(optionColumnOptions.find(c => c.value === v)?.value ?? '') ? 2 : 1
            const columns: CustomColumnDef<optionTradeDataType>[] = []
            for (let s = 0; s < colSpan; s++) {
                const subColumn = optionColumnOptions.find(c => c.value === v)?.value?.split('|')?.[s]
                const columnWidthUnit = colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1
                const column: CustomColumnDef<optionTradeDataType> = {
                    header:
                        s === 0 ? (
                            <Typography sx={{ padding: '0px 4px !important', fontSize: '14px', display: 'flex', justifyContent: "right" }}>
                                {optionColumnOptions.find((c) => c.value === v)?.label}
                                {(v === 'openInterest' || v === 'volume' || v === 'oiChange|outlook') && quantityInLots ? ' (lots)' : null}
                            </Typography>
                        ) : null,
                    field: 'callOption',
                    cellIdentityKey: getOptionChainCellIdentityKey(v, colSpan > 1 ? subColumn : undefined),
                    cellRenderer: (row) => (
                        <ColumnElementRender
                            text={row.callOption}
                            columnId={v}
                            side={'left'}
                            type={showSingleExpiry ? 'extended' : 'normal'}
                            record={row}
                            subColumn={colSpan > 1 ? subColumn : undefined}
                            maxOiChange={expiryToMaxOiChange ?? [0, 0]}
                        />),
                    sx: {
                        padding: 0,
                        whiteSpace: 'nowrap',
                        height: '33px !important',
                        width: 'auto',
                        minWidth: `${colMinWidth[v] ?? 61.5}px`,
                    },
                    colSpan: s === 0 ? colSpan : 0,
                    // tableCellClassName: 'highlight-calls'
                }
                columns.push(column)
            }
            return columns
        })]
            : [])
            .flat() as CustomColumnDef<optionTradeDataType>[],
        ...(type === 'strikeCol'
            ? [
                {
                    header: (
                        <Typography
                            display={'flex'}
                            alignItems={'center'}
                            justifyContent={'center'}
                            sx={{ padding: '0px', fontSize: '14px', textAlign: 'center', height: showSingleExpiry ? '21px !important' : 'auto' }}
                        >
                            Strikes
                        </Typography>
                    ),
                    field: 'strikeCol',
                    cellRenderer: (row, i) => (
                        <CustomRenderStrike
                            obj={row.strikeCol}
                            record={row}
                            i={i}
                            priceMultiplier={security?.priceMultiplier ?? 100}
                            strikeRoundingRequired={strikeRoundingRequired}
                            atmStrike={record.atmStrike}
                        />
                    ),
                    align: 'center',
                    sx: {
                        padding: 0,
                        width: 'auto',
                        minWidth: '100px',
                        cursor: 'pointer',
                        height: '33px !important',
                    },
                    cellSx: {
                        height: '33px !important',
                        lineHeight: '33px',
                    },
                } as CustomColumnDef<optionTradeDataType>,
            ]
            : []),

        // ...(showSingleExpiry ? [] :
        //     [
        //         ...showBidsAndAsks ? [
        //             {
        //                 header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Bid</Typography>),
        //                 field: 'putOption',
        //                 cellRenderer: (row) => {
        //                     return <RenderBid
        //                         security={row.putOption?.security}
        //                         rowKey={row.key}
        //                         handleOrderClick={handleOptionOrderClick}
        //                         initialValue={row.putOption?.topBid ?? 0}
        //                         strike={row.strikeCol.strike}
        //                         type="put"
        //                     />
        //                 },
        //                 sx: { padding: 0, width: `${bidAskColumnWidth}%`, cursor: 'pointer', height: '31px' },
        //                 tableCellClassName: 'highlight-puts'
        //             } as CustomColumnDef<optionTradeDataType>] : [],
        //         ...(showBidsAndAsks) ? [{
        //             header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Ask</Typography>),
        //             field: 'putOption',
        //             cellRenderer: (row) => {
        //                 return <RenderAsk
        //                     security={row.putOption?.security}
        //                     rowKey={row.key}
        //                     handleOrderClick={handleOptionOrderClick}
        //                     initialValue={row.putOption?.topAsk ?? 0}
        //                     strike={row.strikeCol.strike}
        //                     type="put"
        //                     illiquid={row.putOption?.illiquid}
        //                 />
        //             },
        //             sx: { padding: 0, width: `${bidAskColumnWidth}%`, cursor: 'pointer', height: '31px' },
        //             tableCellClassName: 'highlight-puts'
        //         } as CustomColumnDef<optionTradeDataType>] : []
        //     ]),
        ...(type === 'putOption' && noOfColumnsVisible > 0 ? [...([...selectedColumns]).reverse().map((v: string, i: number, arr: string[]) => {
            const currentindex = i
            const val = v
            const colSpan = spanCols.includes(optionColumnOptions.find(c => c.value === val)?.value ?? '') ? 2 : 1
            const columns: CustomColumnDef<optionTradeDataType>[] = []
            for (let s = 0; s < colSpan; s++) {
                const subColumn = optionColumnOptions.find(c => c.value === val)?.value?.split('|')?.[s]
                const columnWidthUnit = colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1
                const column: CustomColumnDef<optionTradeDataType> = {
                    header:
                        s === 0 ? (
                            <Typography sx={{ padding: '0px 4px !important', fontSize: '14px', display: 'flex', justifyContent: "right" }}>
                                {optionColumnOptions.find((c) => c.value === val)?.label}
                                {(val === 'openInterest' || val === 'volume' || val === 'oiChange|outlook') && quantityInLots ? ' (lots)' : null}
                            </Typography>
                        ) : null,
                    field: 'putOption',
                    cellIdentityKey: getOptionChainCellIdentityKey(val, colSpan > 1 ? subColumn : undefined),
                    cellRenderer: (row) => (
                        <ColumnElementRender text={row.putOption}
                            columnId={val}
                            side={'right'}
                            type={showSingleExpiry ? 'extended' : 'normal'}
                            record={row} subColumn={colSpan > 1 ? subColumn : undefined}
                            maxOiChange={expiryToMaxOiChange ?? [0, 0]}
                        />),
                    sx: {
                        padding: 0,
                        whiteSpace: 'nowrap',
                        height: '33px !important',
                        width: 'auto',
                        minWidth: `${colMinWidth[val] ?? 61.5}px`,
                    },
                    colSpan: s === 0 ? colSpan : 0,
                    // tableCellClassName: 'highlight-puts'
                }
                columns.push(column)
            }
            return columns
        })].reverse()
            // .slice(0, noOfColumnsVisible) 
            : []).flat() as CustomColumnDef<optionTradeDataType>[]
    ] : selectedOptionChainTableView === OptionChainTableView.PUTS ? [
        {
            header: (<Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>Strikes</Typography>),
            field: 'strikeCol',
            cellRenderer: (row, i) => (
                <CustomRenderStrike
                    obj={row.strikeCol}
                    record={row}
                    i={i}
                    priceMultiplier={security?.priceMultiplier ?? 100}
                    strikeRoundingRequired={strikeRoundingRequired}
                    atmStrike={record.atmStrike}
                />
            ),

            align: 'center',
            sx: { padding: 0, width: '120px', cursor: 'pointer', height: '31px ', minWidth: '100px', maxWidth: "120px" },
            tableCellClassName: 'sticky-col'
        } as CustomColumnDef<optionTradeDataType>,

        // {
        //     header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Bid</Typography>),
        //     field: 'putOption',
        //     cellRenderer: (row) => {
        //         return <RenderBid
        //             security={row.putOption?.security}
        //             rowKey={row.key}
        //             handleOrderClick={handleOptionOrderClick}
        //             initialValue={row.putOption?.topBid ?? 0}
        //             strike={row.strikeCol.strike}
        //             type="put"
        //         />
        //     },
        //     sx: { padding: 0, width: bidAskColumnWidth, cursor: 'pointer', height: '31px', minWidth: "auto" },
        //     tableCellClassName: 'highlight-puts'
        // } as CustomColumnDef<optionTradeDataType>,
        // {
        //     header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Ask</Typography>),
        //     field: 'putOption',
        //     cellRenderer: (row) => {
        //         return <RenderAsk
        //             security={row.putOption?.security}
        //             rowKey={row.key}
        //             handleOrderClick={handleOptionOrderClick}
        //             initialValue={row.putOption?.topAsk ?? 0}
        //             strike={row.strikeCol.strike}
        //             type="put"
        //             illiquid={row.putOption?.illiquid}
        //         />
        //     },
        //     sx: { padding: 0, width: 'auto', cursor: 'pointer', height: '31px', minWidth: bidAskColumnWidth },
        //     tableCellClassName: 'highlight-puts'
        // } as CustomColumnDef<optionTradeDataType>,
        ...(type === 'putOption' && noOfColumnsVisible > 0 ? [...(selectedColumns).map((v: string, i: number, arr: string[]) => {


            const currentindex = i
            const val = v
            const colSpan = spanCols.includes(optionColumnOptions.find(c => c.value === val)?.value ?? '') ? 2 : 1
            const columns: CustomColumnDef<optionTradeDataType>[] = []
            for (let s = 0; s < colSpan; s++) {
                const subColumn = optionColumnOptions.find(c => c.value === val)?.value?.split('|')?.[s]
                const columnWidthUnit = colsWidth(showSingleExpiry)[subColumn ?? ''] ?? 1
                const column: CustomColumnDef<optionTradeDataType> = {
                    header: s === 0 ? (
                        <>
                            {
                                optionColumnOptions.find(c => c.value === val)?.label
                            }
                            {
                                (val === 'openInterest' || val === 'oiChange|outlook') && quantityInLots ? ' (lots)' : null
                            }
                        </>
                    ) : null,
                    field: 'putOption',
                    cellIdentityKey: getOptionChainCellIdentityKey(val, colSpan > 1 ? subColumn : undefined),
                    cellRenderer: (row) => (
                        <ColumnElementRender text={row.putOption}
                            columnId={val}
                            side={'right'}
                            type={showSingleExpiry ? 'extended' : 'normal'}
                            record={row} subColumn={colSpan > 1 ? subColumn : undefined}
                            maxOiChange={expiryToMaxOiChange ?? [0, 0]}
                        />),
                    sx: {
                        height: '33px !important',
                        padding: 0,
                        whiteSpace: 'nowrap',
                        width: 'auto',
                        minWidth: `${colMinWidth[val] ?? 61.5}px`,
                    },
                    colSpan: s === 0 ? colSpan : 0,
                    // tableCellClassName: 'highlight-puts'
                }
                columns.push(column)
            }
            return columns
        })]
            // .slice(0, noOfColumnsVisible)
            : []).flat() as CustomColumnDef<optionTradeDataType>[]
    ] : selectedOptionChainTableView === OptionChainTableView.CALLS ? [
        {
            header: (<Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>Strikes</Typography>),
            field: 'strikeCol',
            cellRenderer: (row, i) => (
                <CustomRenderStrike
                    obj={row.strikeCol}
                    record={row}
                    i={i}
                    priceMultiplier={security?.priceMultiplier ?? 100}
                    strikeRoundingRequired={strikeRoundingRequired}
                    atmStrike={record.atmStrike}
                />
            ),

            align: 'center',
            sx: { padding: 0, width: '120px', cursor: 'pointer', height: '31px', minWidth: strikeColWidth, maxWidth: "120px" },
            tableCellClassName: 'sticky-col'
        } as CustomColumnDef<optionTradeDataType>,
        // {
        //     header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Bid</Typography>),
        //     field: 'callOption',
        //     cellRenderer: (row) => {
        //         return <RenderBid
        //             security={row.callOption?.security}
        //             rowKey={row.key}
        //             handleOrderClick={handleOptionOrderClick}
        //             initialValue={row.callOption?.topBid ?? 0}
        //             strike={row.strikeCol.strike}
        //             type="call"
        //             illiquid={row.callOption?.illiquid}
        //         />
        //     },
        //     sx: { padding: 0, width: 'auto', cursor: 'pointer', height: '31px', minWidth: bidAskColumnWidth },

        //     // tableCellClassName: 'highlight-calls'
        // } as CustomColumnDef<optionTradeDataType>,
        // {
        //     header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Ask</Typography>),
        //     field: 'callOption',
        //     cellRenderer: (row) => {
        //         return <RenderAsk
        //             security={row.callOption?.security}
        //             rowKey={row.key}
        //             handleOrderClick={handleOptionOrderClick}
        //             initialValue={row.callOption?.topAsk ?? 0}
        //             strike={row.strikeCol.strike}
        //             type="call"
        //         />
        //     },
        //     sx: { padding: 0, width: 'auto', cursor: 'pointer', height: '31px', minWidth: bidAskColumnWidth },
        //     // tableCellClassName: 'highlight-calls'
        // } as CustomColumnDef<optionTradeDataType>,
        ...(noOfColumnsVisible > 0 ? [...([...selectedColumns].reverse()).map((_v: string, i: number, arr: string[]) => {
            const currentindex = arr.length - i - 1
            const val = arr[currentindex]
            const colSpan = spanCols.includes(optionColumnOptions.find(c => c.value === val)?.value ?? '') ? 2 : 1
            const columns: CustomColumnDef<optionTradeDataType>[] = []
            for (let s = 0; s < colSpan; s++) {
                const subColumn = optionColumnOptions.find(c => c.value === val)?.value?.split('|')?.[s]
                const column: CustomColumnDef<optionTradeDataType> = {
                    header: s === 0 ? (
                        <>
                            {
                                optionColumnOptions.find(c => c.value === val)?.label
                            }
                            {
                                val === 'openInterest' && quantityInLots ? ' (lots)' : null
                            }
                        </>
                    ) : null,
                    field: 'callOption',
                    cellIdentityKey: getOptionChainCellIdentityKey(val, colSpan > 1 ? subColumn : undefined),
                    cellRenderer: (row) => (
                        <ColumnElementRender text={row.callOption} columnId={val}
                            side={'right'}
                            type={showSingleExpiry ? 'extended' : 'normal'}
                            record={row} subColumn={colSpan > 1 ? subColumn : undefined}
                            maxOiChange={expiryToMaxOiChange ?? [0, 0]}
                        />),
                    sx: {
                        height: '33px !important',
                        padding: 0,
                        width: 'auto',
                        minWidth: `${colMinWidth[subColumn ?? 'ltp'] ?? 80}px`
                    },
                    colSpan: s === 0 ? colSpan : 0,
                    // tableCellClassName: 'highlight-calls'
                }
                columns.push(column)
            }
            return columns
        })]
            // .slice(0, noOfColumnsVisible) 
            : []).flat() as CustomColumnDef<optionTradeDataType>[]
    ] : [
        {
            header: (<Typography sx={{ padding: '0px', fontSize: '14px', textAlign: 'center' }}>Strikes</Typography>),
            field: 'strikeCol',
            cellRenderer: (row, i) => (
                <CustomRenderStrike
                    obj={row.strikeCol}
                    record={row}
                    i={i}
                    priceMultiplier={security?.priceMultiplier ?? 100}
                    strikeRoundingRequired={strikeRoundingRequired}
                    atmStrike={record.atmStrike}
                />
            ),

            align: 'center',
            sx: { padding: 0, width: '120px', cursor: 'pointer', height: '31px', minWidth: strikeColWidth, maxWidth: "120px" },
            tableCellClassName: 'sticky-col'

        } as CustomColumnDef<optionTradeDataType>,
        // {
        //     header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Bid</Typography>),
        //     field: selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption',
        //     cellRenderer: (row) => {
        //         return <RenderBid
        //             security={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']?.security}
        //             rowKey={row.key}
        //             handleOrderClick={handleOptionOrderClick}
        //             initialValue={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']?.topBid ?? 0}
        //             strike={row.strikeCol.strike}
        //             type={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']?.security?.optionType === OptionType.CE ? 'call' : 'put'}
        //         />
        //     },
        //     sx: { padding: 0, width: 'auto', cursor: 'pointer', height: '31px', minWidth: bidAskColumnWidth }
        // } as CustomColumnDef<optionTradeDataType>,
        // {
        //     header: (<Typography variant={'body1'} sx={{ padding: '3px 5px 3px 5px' }}>Ask</Typography>),
        //     field: selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption',
        //     cellRenderer: (row) => {
        //         return <RenderAsk
        //             security={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']?.security}
        //             rowKey={row.key}
        //             handleOrderClick={handleOptionOrderClick}
        //             initialValue={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']?.topAsk ?? 0}
        //             strike={row.strikeCol.strike}
        //             type={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']?.security?.optionType === OptionType.CE ? 'call' : 'put'}
        //         />
        //     },
        //     sx: { padding: 0, width: 'auto', cursor: 'pointer', height: '31px', minWidth: bidAskColumnWidth }
        // } as CustomColumnDef<optionTradeDataType>,
        ...(noOfColumnsVisible > 0 ? [...([...selectedColumns].reverse()).map((_v: string, i: number, arr: string[]) => {
            const currentindex = arr.length - i - 1
            const val = arr[currentindex]
            const colSpan = spanCols.includes(optionColumnOptions.find(c => c.value === val)?.value ?? '') ? 2 : 1
            const columns: CustomColumnDef<optionTradeDataType>[] = []
            for (let s = 0; s < colSpan; s++) {
                const subColumn = optionColumnOptions.find(c => c.value === val)?.value?.split('|')?.[s]
                const column: CustomColumnDef<optionTradeDataType> = {
                    header: s === 0 ? (
                        <>
                            {
                                optionColumnOptions.find(c => c.value === val)?.label
                            }
                            {
                                val === 'openInterest' && quantityInLots ? ' (lots)' : null
                            }
                        </>
                    ) : null,
                    field: selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption',
                    cellIdentityKey: getOptionChainCellIdentityKey(val, colSpan > 1 ? subColumn : undefined),
                    cellRenderer: (row) => (
                        <ColumnElementRender text={row?.[selectedOptionChainTableView === OptionChainTableView.ITM ? 'itmOption' : 'otmOption']} columnId={val}
                            side={'right'}
                            type={showSingleExpiry ? 'extended' : 'normal'}
                            record={row} subColumn={colSpan > 1 ? subColumn : undefined}
                            maxOiChange={expiryToMaxOiChange ?? [0, 0]}
                        />),
                    sx: {
                        height: '33px !important',
                        padding: 0,
                        width: 'auto',
                        minWidth: `${colMinWidth[val] ?? 61.5}px`,
                    },
                    colSpan: s === 0 ? colSpan : 0
                }
                columns.push(column)
            }
            return columns
        })]: []
        ).flat() as CustomColumnDef<optionTradeDataType>[]
    ], [selectedOptionChainTableView, noOfColumnsVisible, type, selectedColumns, bidAskColumnWidth, strikeColWidth, quantityInLots, expiryToMaxPricePercentChange, expiryToMaxOiChange, security?.priceMultiplier, strikeRoundingRequired, showSingleExpiry, isMirrorScroll])

    const renderEmptyData = useCallback((loading: boolean, colSpan: number) => {
        return (
            <tr>
                <td colSpan={colSpan} style={{ height: '200px' }}>
                    {
                        loading ?
                            <Loading size={30} /> :
                            <p style={{ textAlign: 'center' }}>No Data Found</p>
                    }
                </td>
            </tr>
        )
    }, [])

    const handleRowClick = useCallback((row: optionTradeDataType, clickType: 'call' | 'put' | 'itm' | 'otm') => {
        if (!addOrderEnabled) return;
        const strike = row.strikeCol.strike;
        const primarySide: 'call' | 'put' = (row.callOption?.openInterest ?? 0) >= (row.putOption?.openInterest ?? 0) ? 'call' : 'put';
        const side = clickType === 'itm' || clickType === 'otm' ? primarySide : clickType;
        const state = store.getState()
        const currentOrderType = isOptionChainLiveView(optionChainViewKey)
            ? selectOptionChainRowOrderSide(state, row.key, side)
            : historicalOrderMap?.[row.key]?.[side]
        let nextType: 'buy' | 'sell' | undefined;
        if (!currentOrderType) nextType = 'buy';
        else if (currentOrderType === 'buy') nextType = 'sell';
        else nextType = undefined;
        handleOptionOrderClick(nextType, strike, clickType === 'itm' || clickType === 'otm' ? primarySide : clickType);
    },
        [handleOptionOrderClick, addOrderEnabled, optionChainViewKey, historicalOrderMap]
    );

    const rowClickSide = useMemo((): 'call' | 'put' | 'itm' | 'otm' | undefined => {
        if (type === 'callOption') return 'call'
        if (type === 'putOption') return 'put'
        if (type === 'otmOption') return 'otm'
        if (type === 'itmOption') return 'itm'
        return undefined
    }, [type])

    const handleTableRowClick = useCallback(
        (row: optionTradeDataType, _index: number) => {
            if (!rowClickSide) return
            handleRowClick(row, rowClickSide)
        },
        [handleRowClick, rowClickSide]
    )

    const OptionChainRow = useMemo(() => {
        return function BoundOptionChainRow(props: TableRowComponentProps<optionTradeDataType>) {
            return (
                <OptionChainTableRow
                    {...props}
                    tableColumnType={type}
                    optionChainViewKey={optionChainViewKey}
                />
            )
        }
    }, [type, optionChainViewKey])

    const tableSxProps = useMemo(()=>({
        tableLayout: 'auto !important',
    }), [])

    return (
        <OptionChainOrderHighlightContext.Provider value={historicalOrderMap}>
        <OptionChainOrderClickContext.Provider value={stableHandleOptionOrderClick}>
        <OptionChainPositionContext.Provider value={strikeToPositionMap}>
        <CustomTable
            hideHeader
            className="custom-table-body option-chain-table"
            columns={strikeColumns}
            rows={record.trades}
            verticalDividers
            TableRowComponent={OptionChainRow}
            onRowClick={showSingleExpiry || !rowClickSide ? undefined : handleTableRowClick}
            keyFieldInData="key"
            getRowClassName={getRowClassName}
            loading={record.loading}
            disableColGroup
            renderEmptyData={renderEmptyData}
            tableSxProps={tableSxProps}
            singleViewTable={selectedOptionChainTableView !== OptionChainTableView.NORMAL}
            strikeColWidth={strikeColWidth}
            skeletalLoading={skeletalLoading}
            stableLiveContent
        />
        </OptionChainPositionContext.Provider>
        </OptionChainOrderClickContext.Provider>
        </OptionChainOrderHighlightContext.Provider>
    )
}

export default OptionChainExpandedTable